import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Avatar,
  IconButton,
  Grid,
} from "@mui/material";
import { Add, Edit, Delete } from "@mui/icons-material";
import axiosInstance from "../utils/axios";
import { toast } from "react-toastify";

export default function AdminDeliveryBoys() {
  const [deliveryBoys, setDeliveryBoys] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    city: "",
    address: "",
    vehicleType: "Bike",
    vehicleNumber: "",
  });

  const [profileImageFile, setProfileImageFile] = useState(null);
  const [aadharCardFile, setAadharCardFile] = useState(null);
  const [existingImages, setExistingImages] = useState({ profile: "", aadhar: "" });

  const fetchDeliveryBoys = async () => {
    try {
      const res = await axiosInstance.get("/api/delivery/admin/list");
      setDeliveryBoys(res.data.deliveryBoys || []);
    } catch (err) {
      toast.error("Failed to load delivery boys");
    }
  };

  useEffect(() => {
    fetchDeliveryBoys();
  }, []);

  const handleOpenAdd = () => {
    setEditingId(null);
    setFormData({
      name: "",
      email: "",
      phone: "",
      password: "",
      city: "",
      address: "",
      vehicleType: "Bike",
      vehicleNumber: "",
    });
    setProfileImageFile(null);
    setAadharCardFile(null);
    setExistingImages({ profile: "", aadhar: "" });
    setOpenDialog(true);
  };

  const handleOpenEdit = (boy) => {
    setEditingId(boy._id);
    setFormData({
      name: boy.name || "",
      email: boy.email || "",
      phone: boy.phone || "",
      password: "",
      city: boy.city || "",
      address: boy.address || "",
      vehicleType: boy.vehicleType || "Bike",
      vehicleNumber: boy.vehicleNumber || "",
    });
    setProfileImageFile(null);
    setAadharCardFile(null);
    setExistingImages({
      profile: boy.profileImage?.url || "",
      aadhar: boy.aadharCard?.url || "",
    });
    setOpenDialog(true);
  };

  // 🔥 MAIN FIX: FormData with multipart/form-data header
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = new FormData();

      data.append("name", formData.name);
      data.append("email", formData.email);
      data.append("phone", formData.phone);
      data.append("city", formData.city);
      data.append("address", formData.address || "");
      data.append("vehicleType", formData.vehicleType || "Bike");
      data.append("vehicleNumber", formData.vehicleNumber || "");

      // Password: Naye registration me zaroori hai, edit me sirf tab jab fill kiya ho
      if (formData.password && formData.password.trim() !== "") {
        data.append("password", formData.password);
      }

      // Files append
      if (profileImageFile) {
        data.append("profileImage", profileImageFile);
      }
      if (aadharCardFile) {
        data.append("aadharCard", aadharCardFile);
      }

      // 🔥 Header specifically multipart/form-data set karein:
      const config = {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      };

      if (editingId) {
        await axiosInstance.put(`/api/delivery/admin/${editingId}/update`, data, config);
        toast.success("Delivery boy updated successfully!");
      } else {
        await axiosInstance.post("/api/delivery/admin/register", data, config);
        toast.success("Delivery boy registered successfully!");
      }

      setOpenDialog(false);
      fetchDeliveryBoys();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Operation failed");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      const res = await axiosInstance.put(`/api/delivery/admin/${id}/toggle-status`);
      toast.success(res.data.message);
      fetchDeliveryBoys();
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this delivery boy?")) return;
    try {
      await axiosInstance.delete(`/api/delivery/admin/${id}`);
      toast.success("Delivery boy deleted");
      fetchDeliveryBoys();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight="bold">
          Delivery Boys Management
        </Typography>
        <Button variant="contained" startIcon={<Add />} onClick={handleOpenAdd}>
          Add Delivery Boy
        </Button>
      </Box>

      <TableContainer component={Paper} elevation={1}>
        <Table>
          <TableHead sx={{ bgcolor: "grey.100" }}>
            <TableRow>
              <TableCell><strong>Photo</strong></TableCell>
              <TableCell><strong>Name & Details</strong></TableCell>
              <TableCell><strong>Assigned City</strong></TableCell>
              <TableCell><strong>Address</strong></TableCell>
              <TableCell><strong>Aadhar Card</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell align="center"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {deliveryBoys.map((boy) => (
              <TableRow key={boy._id}>
                <TableCell>
                  <Avatar
                    src={boy.profileImage?.url}
                    alt={boy.name}
                    sx={{ width: 45, height: 45 }}
                  />
                </TableCell>
                <TableCell>
                  <Typography fontWeight="bold">{boy.name}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {boy.phone} | {boy.email}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Vehicle: {boy.vehicleType} ({boy.vehicleNumber || "N/A"})
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip label={boy.city?.toUpperCase()} color="primary" size="small" variant="outlined" />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{boy.address || "-"}</Typography>
                </TableCell>
                <TableCell>
                  {boy.aadharCard?.url ? (
                    <a href={boy.aadharCard.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                      <Chip label="View Aadhar" color="info" size="small" clickable />
                    </a>
                  ) : (
                    <Typography variant="caption" color="text.secondary">Not Uploaded</Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Button
                    size="small"
                    variant="text"
                    color={boy.isActive ? "success" : "inherit"}
                    onClick={() => handleToggleStatus(boy._id)}
                  >
                    {boy.isActive ? "Active" : "Inactive"}
                  </Button>
                </TableCell>
                <TableCell align="center">
                  <IconButton color="primary" onClick={() => handleOpenEdit(boy)}>
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton color="error" onClick={() => handleDelete(boy._id)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {deliveryBoys.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">No delivery boys found.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add / Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingId ? "Edit Delivery Boy" : "Register New Delivery Boy"}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={editingId ? "New Password (leave blank to keep current)" : "Password"}
                  type="password"
                  required={!editingId}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Assigned City (e.g. jaipur)"
                  required
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Vehicle Number"
                  value={formData.vehicleNumber}
                  onChange={(e) => setFormData({ ...formData, vehicleNumber: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Full Address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </Grid>

              {/* Photo Upload Section */}
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" gutterBottom>Profile Image:</Typography>
                
                {/* Preview Selected or Existing Image */}
                {profileImageFile ? (
                  <Box mb={1}>
                    <img
                      src={URL.createObjectURL(profileImageFile)}
                      alt="New Profile Preview"
                      style={{ width: 70, height: 70, borderRadius: "50%", objectFit: "cover", border: "2px solid #1976d2" }}
                    />
                  </Box>
                ) : existingImages.profile ? (
                  <Box mb={1}>
                    <img
                      src={existingImages.profile}
                      alt="Current Profile"
                      style={{ width: 70, height: 70, borderRadius: "50%", objectFit: "cover" }}
                    />
                  </Box>
                ) : null}

                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setProfileImageFile(e.target.files[0]);
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" gutterBottom>Aadhar Card Photo:</Typography>
                
                {/* Preview Selected or Existing Aadhar */}
                {aadharCardFile ? (
                  <Box mb={1}>
                    <img
                      src={URL.createObjectURL(aadharCardFile)}
                      alt="New Aadhar Preview"
                      style={{ width: 100, height: 60, objectFit: "cover", borderRadius: 4, border: "2px solid #1976d2" }}
                    />
                  </Box>
                ) : existingImages.aadhar ? (
                  <Box mb={1}>
                    <a href={existingImages.aadhar} target="_blank" rel="noopener noreferrer">
                      <img
                        src={existingImages.aadhar}
                        alt="Current Aadhar"
                        style={{ width: 100, height: 60, objectFit: "cover", borderRadius: 4 }}
                      />
                    </a>
                  </Box>
                ) : null}

                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setAadharCardFile(e.target.files[0]);
                    }
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? "Uploading & Saving..." : editingId ? "Update" : "Register"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}